import { Input } from '@/components/shadcn/input';
import { useState } from 'react';
import { PostReview } from '@/__generated__/graphql';
import SettingsTabTitle from '@/components/compound-components/settings/settings-tab-title';
import { gql } from '@/__generated__';
import { useSuspenseQuery } from '@apollo/client';
import { useOrganizationContext } from '@/contexts/organization-context';
import { logError } from '@/lib/rum';

interface CustomerReviewSectionProps {
  onOrganizationChange: (value: PostReview) => void;
}

const OrganizationQuery = gql(`
  query QueryGetOrganizationById($input: OrganizationGetByIdInput) {
    organization {
      getById(input: $input) {
        ... on OrganizationGetByIdSuccess {
          result {
            id
            postReview {
              daysAhead
              daysBack
            }
          }
        }
      }
    }
  }
`);

const CustomerReviewSection = ({
  onOrganizationChange,
}: CustomerReviewSectionProps) => {
  const { organizationId } = useOrganizationContext();

  const maxDays = 180; // 6 months just for safety measure

  const { data: orgData, error } = useSuspenseQuery(OrganizationQuery, {
    variables: {
      input: { id: organizationId || '' },
    },
  });

  const initialDaysBack =
    orgData?.organization?.getById?.__typename === 'OrganizationGetByIdSuccess'
      ? orgData.organization.getById.result.postReview?.daysBack
      : undefined;

  const initialDaysAhead =
    orgData?.organization?.getById?.__typename === 'OrganizationGetByIdSuccess'
      ? orgData.organization.getById.result.postReview?.daysAhead
      : undefined;

  const [daysBack, setDaysBack] = useState<number | undefined>(initialDaysBack);
  const [daysAhead, setDaysAhead] = useState<number | undefined>(
    initialDaysAhead
  );

  const onChangePostReview = (daysBack: number, daysAhead: number) => {
    onOrganizationChange({
      daysBack,
      daysAhead,
    });
  };

  const onChangePostReviewDaysBack = (value: string) => {
    const valueInt = Number.parseInt(value);
    if (valueInt > maxDays || valueInt < 0) {
      return;
    }
    if (daysAhead === undefined) return;
    setDaysBack(Number.parseInt(value));
    onChangePostReview(valueInt, daysAhead);
  };

  const onChangePostReviewDaysAhead = (value: string) => {
    const valueInt = Number.parseInt(value);
    if (valueInt > maxDays || valueInt < 0) {
      return;
    }
    if (daysBack === undefined) return;
    setDaysAhead(Number.parseInt(value));
    onChangePostReview(daysBack, valueInt);
  };

  if (error) {
    logError(
      'Something went wrong while loading organization grant settings',
      error
    );
  }

  return (
    <div className='mt-6'>
      <SettingsTabTitle title='Customer Review' />
      <p className='-mt-2 mb-2'>
        Post Review Link time window (max days value {maxDays}):
      </p>
      <div className='flex gap-8'>
        <div className='flex-1'>
          <p>Days Back</p>
          <div>
            <Input
              type='number'
              value={daysBack}
              onChange={(e) => onChangePostReviewDaysBack(e.target.value)}
            />
          </div>
        </div>
        <div className='flex-1'>
          <p>Days Ahead from Start Date</p>
          <div>
            <Input
              type='number'
              value={daysAhead}
              onChange={(e) => onChangePostReviewDaysAhead(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReviewSection;
